<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Please provide details about the trust account. This information is
          required by your advisor and the custodian to complete the account setup.
        </p>
      </div>
    </help-drawer>

    <v-form name="TrustAccount" class="form" data-test-id="trust-account-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.TRUST_ACCOUNT.id" :instance="instance" />

        <!-- Heading -->
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="trust-account-header">
              <span class="headerlight">
                {{ accountName }} <strong>Trust Account Details</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>

        <!-- Sync message -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving trust account information...</p>
        </div>

        <!-- Re-open form message -->
        <data-submitted-message 
          v-if="isTrustAccountComplete(instance) && formHidden && !isSyncing" 
          :submitted="isTrustAccountComplete(instance)"
          v-on:hide-form="toggleFormState" 
          data-test-id="data-submitted-message"
        />

        <!-- === FORM === -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container">
              <!-- Trust Name -->
              <label for="trustName" class="required" data-test-id="trust-name-label">
                Full Legal Name of Trust
              </label>
              <v-text-field
                outlined
                type="text"
                maxlength="256"
                name="trustName"
                aria-required="true"
                v-model.trim="trustName"
                :backgroundColor="inputBackgroundColor"
                @input="$v.trustName.$touch()"
                @blur="$v.trustName.$touch()"
                :error-messages="trustNameErrors"
                hide-details="auto"
                class="mb-2"
                placeholder="Example: The John Doe Revocable Living Trust"
                data-test-id="trust-name-input"
              />

              <!-- Trust Date -->
              <label for="trustDate" class="required" data-test-id="trust-date-label">
                Date Trust was Created
              </label>
              <v-text-field
                v-model="trustDate"
                :placeholder="INPUT_DATE_FMT"
                outlined
                name="trustDate"
                maxlength="8"
                aria-required="true"
                :backgroundColor="inputBackgroundColor"
                @input="$v.trustDate.$touch()"
                @blur="standardizeTrustDate()"
                :error-messages="trustDateErrors"
                hide-details="auto"
                inputmode="numeric"
                class="mb-2"
                data-test-id="trust-date-input"
              >
                <template v-slot:append>
                  <span class="cal-icon" data-test-id="calendar-icon">
                    <v-icon small color="#9b9b9b">fas fa-calendar-alt</v-icon>
                  </span>
                </template>
              </v-text-field>

              <!-- Trustee Information -->
              <h3 class="section-header mt-4 mb-2">Trustee Information</h3>
              
              <v-row>
                <v-col>
                  <label for="trusteeFirstName" class="required" data-test-id="trustee-first-name-label">
                    Trustee First Name
                  </label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="trusteeFirstName"
                    aria-required="true"
                    v-model.trim="trusteeFirstName"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.trusteeFirstName.$touch()"
                    @blur="$v.trusteeFirstName.$touch()"
                    :error-messages="trusteeFirstNameErrors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="trustee-first-name-input"
                  />
                </v-col>
                <v-col>
                  <label for="trusteeLastName" class="required" data-test-id="trustee-last-name-label">
                    Trustee Last Name
                  </label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="trusteeLastName"
                    aria-required="true"
                    v-model.trim="trusteeLastName"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.trusteeLastName.$touch()"
                    @blur="$v.trusteeLastName.$touch()"
                    :error-messages="trusteeLastNameErrors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="trustee-last-name-input"
                  />
                </v-col>
              </v-row>

              <!-- Co-Trustee (Optional) -->
              <v-checkbox
                hide-details
                :ripple="false"
                v-model="hasCoTrustee"
                :color="primaryColor"
                @change="coTrusteeChanged()"
                class="mt-2 mb-4"
                data-test-id="co-trustee-checkbox"
              >
                <template v-slot:label>
                  <span class="cb-label">Add Co-Trustee</span>
                </template>
              </v-checkbox>

              <v-row v-if="hasCoTrustee">
                <v-col>
                  <label for="coTrusteeFirstName" class="required" data-test-id="co-trustee-first-name-label">
                    Co-Trustee First Name
                  </label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="coTrusteeFirstName"
                    aria-required="true"
                    v-model.trim="coTrusteeFirstName"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.coTrusteeFirstName.$touch()"
                    @blur="$v.coTrusteeFirstName.$touch()"
                    :error-messages="coTrusteeFirstNameErrors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="co-trustee-first-name-input"
                  />
                </v-col>
                <v-col>
                  <label for="coTrusteeLastName" class="required" data-test-id="co-trustee-last-name-label">
                    Co-Trustee Last Name
                  </label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="coTrusteeLastName"
                    aria-required="true"
                    v-model.trim="coTrusteeLastName"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.coTrusteeLastName.$touch()"
                    @blur="$v.coTrusteeLastName.$touch()"
                    :error-messages="coTrusteeLastNameErrors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="co-trustee-last-name-input"
                  />
                </v-col>
              </v-row>

              <!-- Tax Information -->
              <h3 class="section-header mt-4 mb-2">Tax Information</h3>
              
              <!-- EIN -->
              <label for="ein" class="required" data-test-id="ein-label">
                Employer Identification Number (EIN)
              </label>
              <v-text-field
                outlined
                type="text"
                maxlength="10"
                name="ein"
                aria-required="true"
                v-model.trim="ein"
                :backgroundColor="inputBackgroundColor"
                @input="formatEIN"
                @blur="$v.ein.$touch()"
                :error-messages="einErrors"
                hide-details="auto"
                placeholder="12-3456789"
                inputmode="numeric"
                class="mb-2"
                data-test-id="ein-input"
              />

              <!-- Trust Type -->
              <label for="trustType" class="required" data-test-id="trust-type-label">
                Type of Trust
              </label>
              <v-select
                v-model="trustType"
                :items="trustTypeOptions"
                :backgroundColor="inputBackgroundColor"
                outlined
                placeholder="Choose Trust Type"
                dense
                hide-details="auto"
                @change="$v.trustType.$touch()"
                :error-messages="trustTypeErrors"
                class="mb-2"
                data-test-id="trust-type-select"
              />

              <!-- Skip for now -->
              <div class="skip-for-now">
                <a @click="skip()" data-test-id="skip-button">Skip for now</a>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </v-form>

    <bottom-nav @go-prev="prev()" @go-next="next()" data-test-id="trust-account-nav" />
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import {
  MENUS,
  INPUT_DATE_FMT,
  US_DATE_SHORT,
  ISO_DATE_SHORT,
  validInputDate,
  DB_ACCOUNT_TYPES,
} from '@/common/constants';
import { required, requiredIf } from 'vuelidate/lib/validators';

// Custom validator for EIN format
const validateEIN = (value) => {
  if (!value) return true; // Let required validator handle empty
  const cleanEIN = value.replace(/[^0-9]/g, '');
  return cleanEIN.length === 9;
};

export default {
  name: 'TrustAccount',
  props: {
    name: String,
    label: String,
  },
  components: {
    HelpDrawer,
    BottomNav,
    DataSubmittedMessage,
    SyncingIndicator,
  },
  mixins: [InterviewClientMixin],

  data() {
    return {
      MENUS,
      INPUT_DATE_FMT,

      /* form fields */
      trustName: null,
      trustDate: null,
      trusteeFirstName: null,
      trusteeLastName: null,
      hasCoTrustee: false,
      coTrusteeFirstName: null,
      coTrusteeLastName: null,
      ein: null,
      trustType: null,

      /* Options */
      trustTypeOptions: [
        'Revocable Trust, All Individuals',
        'Other Revocable Trust',
        'Irrevocable Trust',
        'GRAT Irrevocable Trust',
        'CRUT Irrevocable Trust',
        'CRAT Irrevocable Trust',
        'Charitable',
        'Family',
        'Irrevocable Living',
        'Living',
        'Revocable',
        'Testamentary',
        'Defective Trust',
        'Insurance Trust',
        'QTIP or QDOT Trust',
        'Qualified Domestic Trust',
        'Special Needs Trust',
      ],
    };
  },

  validations() {
    return {
      trustName: { required },
      trustDate: { required, validInputDate },
      trusteeFirstName: { required },
      trusteeLastName: { required },
      coTrusteeFirstName: {
        required: requiredIf(() => this.hasCoTrustee),
      },
      coTrusteeLastName: {
        required: requiredIf(() => this.hasCoTrustee),
      },
      ein: { required, validateEIN },
      trustType: { required },
      validationGroup: [
        'trustName',
        'trustDate',
        'trusteeFirstName',
        'trusteeLastName',
        'coTrusteeFirstName',
        'coTrusteeLastName',
        'ein',
        'trustType',
      ],
    };
  },

  computed: {
    /* Dynamic error messages */
    trustNameErrors() {
      const errors = [];
      if (!this.$v.trustName.$dirty) return errors;
      !this.$v.trustName.required && errors.push('Trust name is required');
      return errors;
    },
    trustDateErrors() {
      const errors = [];
      if (!this.$v.trustDate.$dirty) return errors;
      !this.$v.trustDate.required && errors.push('Trust date is required');
      !this.$v.trustDate.validInputDate && errors.push('Invalid date (MMDDYYYY)');
      return errors;
    },
    trusteeFirstNameErrors() {
      const errors = [];
      if (!this.$v.trusteeFirstName.$dirty) return errors;
      !this.$v.trusteeFirstName.required && errors.push('First name is required');
      return errors;
    },
    trusteeLastNameErrors() {
      const errors = [];
      if (!this.$v.trusteeLastName.$dirty) return errors;
      !this.$v.trusteeLastName.required && errors.push('Last name is required');
      return errors;
    },
    coTrusteeFirstNameErrors() {
      const errors = [];
      if (!this.$v.coTrusteeFirstName.$dirty) return errors;
      !this.$v.coTrusteeFirstName.required && errors.push('Co-Trustee first name is required');
      return errors;
    },
    coTrusteeLastNameErrors() {
      const errors = [];
      if (!this.$v.coTrusteeLastName.$dirty) return errors;
      !this.$v.coTrusteeLastName.required && errors.push('Co-Trustee last name is required');
      return errors;
    },
    einErrors() {
      const errors = [];
      if (!this.$v.ein.$dirty) return errors;
      !this.$v.ein.required && errors.push('EIN is required');
      !this.$v.ein.validateEIN && errors.push('EIN must be 9 digits (XX-XXXXXXX)');
      return errors;
    },
    trustTypeErrors() {
      const errors = [];
      if (!this.$v.trustType.$dirty) return errors;
      !this.$v.trustType.required && errors.push('Trust type is required');
      return errors;
    },
    
    /* Helpers */
    helpTitle() {
      return 'Why do we need this information?';
    },
    accountName() {
      // Map account type to display name
      if (this.name === DB_ACCOUNT_TYPES.OFFLINE_TRUST) {
        return 'Trust';
      }
      if (this.name === DB_ACCOUNT_TYPES.MANAGED_TRUST) {
        return 'Managed Trust';
      }
      return this.name;
    },
    instance() {
      return {
        name: this.name,
        label: this.label
      };
    },
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.TRUST_ACCOUNT.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    isTrustAccountCompleteForInstance() {
      return this.isTrustAccountComplete(this.instance);
    },
  },

  watch: {
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    isTrustAccountCompleteForInstance(newVal) {
      if (newVal) {
        this.formHidden = true;
      }
    },
  },

  methods: {
    /* Date formatter */
    standardizeTrustDate() {
      this.$v.trustDate.$touch();
      if (validInputDate(this.trustDate)) {
        this.trustDate = window
          .moment(this.trustDate, INPUT_DATE_FMT, true)
          .format(US_DATE_SHORT);
      }
    },

    /* EIN formatter */
    formatEIN() {
      // Remove all non-digits
      let value = this.ein.replace(/[^0-9]/g, '');
      
      // Add hyphen after first 2 digits
      if (value.length > 2) {
        value = value.slice(0, 2) + '-' + value.slice(2, 9);
      }
      
      this.ein = value;
      this.$v.ein.$touch();
    },

    /* Co-Trustee checkbox handler */
    coTrusteeChanged() {
      if (!this.hasCoTrustee) {
        // Clear co-trustee fields when unchecked
        this.coTrusteeFirstName = null;
        this.coTrusteeLastName = null;
        this.$v.coTrusteeFirstName.$reset();
        this.$v.coTrusteeLastName.$reset();
      }
    },

    /* Navigation */
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      /* Prepare payload */
      const data = {
        instance: this.instance,
        trustName: this.trustName,
        trustDate: this.trustDate
          ? window.moment(this.trustDate, US_DATE_SHORT, true).format(ISO_DATE_SHORT)
          : null,
        trusteeFirstName: this.trusteeFirstName,
        trusteeLastName: this.trusteeLastName,
        ein: this.ein.replace(/-/g, ''), // Remove hyphen for storage
        trustType: this.trustType,
      };

      // Include co-trustee if present
      if (this.hasCoTrustee) {
        data.hasCoTrustee = true;
        data.coTrusteeFirstName = this.coTrusteeFirstName;
        data.coTrusteeLastName = this.coTrusteeLastName;
      }

      const pageName = MENUS.CLIENT.TRUST_ACCOUNT.id;

      /* Mark page as syncing immediately for better user feedback */
      this.markPageAsSyncing(pageName, this.instance);

      this.completeStep(pageName, data, this.instance)
        .then(() => {
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => { });
    },

    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },

    /* Toggle form visibility */
    toggleFormState(hide) {
      this.formHidden = hide;
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },

    /* Handle sync status changes */
    handleSyncStatusChange() {
      if (this.isTrustAccountComplete(this.instance)) {
        this.formHidden = true;
      }
    },

    /* Check if Trust Account page is complete */
    isTrustAccountComplete(instance) {
      if (!this.clientProgress || !this.clientProgress.progress) return false;

      const page = this.clientProgress.progress.find(
        (p) =>
          p.pageName === MENUS.CLIENT.TRUST_ACCOUNT.id &&
          p.instance === instance.name && 
          p.label === instance.label
      );

      return page ? page.lastCompleted !== null && page.status === 'SYNCED' : false;
    },
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();

    // Determine the current page index based on the pathname
    this.pageIndex = undefined;
    let i = 0;
    for (const page of this.pages) {
      if (
        typeof page === 'object' &&
        page.pathname &&
        window.location.pathname.includes(page.pathname.replaceAll(' ', '%20'))
      ) {
        this.pageIndex = i;
        break;
      }
      i++;
    }

    // Safety fallback if index still undefined
    if (this.pageIndex === undefined) {
      this.pageIndex = this.pages.findIndex((p) => 
        (typeof p === 'string' ? p : p.name) === MENUS.CLIENT.TRUST_ACCOUNT.id
      );
    }

    this.formHidden = this.isTrustAccountCompleteForInstance;
  },

  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      if (this.isTrustAccountComplete(this.instance)) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  },
};
</script>

<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;

  p {
    color: #666;
    font-style: italic;
  }
}

.section-header {
  font-size: 18px;
  font-weight: 600;
  color: var(--primary-color);
}

.cal-icon {
  line-height: 32px;
  margin-right: 8px;
}

.cb-label {
  font-size: 16px;
  color: var(--color-text);
}

/* Override checkbox styles for consistency */
.v-input--checkbox {
  margin-top: 0;
}
</style>
